<template>
	<DataTable
		:value="rows"
		:loading="loading"
		class="p-datatable-sm p-datatable-documents"
		:autoLayout="true"
		columnResizeMode="fit"
		:reorderableColumns="true"
		@row-reorder="onRowReorder"
		@row-dblclick="openEntryDialog($event.data)"
		:scrollable="true"
		scrollHeight="flex"
		:removableSort="true"
		filterDisplay="row"
		v-model:filters="filters"
		@filter="onFilter($event)"
		@sort="onSort($event)"
		:sortField="currentSortField"
		:sortOrder="currentSortOrder"
	>
		<template #loading>
			{{  $t('Squeeze.General.Loading') }}
		</template>
		<template #empty>
			{{  $t('Squeeze.General.NoEntriesFound', { entryName: $t('Squeeze.DocumentClasses.TableColumns') }) }}
		</template>

		<!-- Columns and Filter -->
		<Column :rowReorder="true" style="min-width: 2rem; max-width: 2rem;" />
		<Column field="id" header="ID" filterField="id" :sortable="true" :showFilterMenu="false" style="min-width: 4rem; max-width: 4rem;">
			<template #body="slotProps">
				{{slotProps.data.id}}
			</template>
			<template #filter="{filterModel, filterCallback}">
				<InputText style="min-width: 3rem;" class="p-column-filter filter-field" type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" />
			</template>
		</Column>
		<Column field="name" :header="$t('Squeeze.DocumentClasses.Name')" filterField="name" :frozen="frozenColumns.name" :sortable="true" :showFilterMenu="false" style="min-width: 10rem; z-index: 1;">
			<template #header>
				<i :class="frozenColumns.name ? 'mdi mdi-lock-outline' : 'mdi mdi-lock-open-variant-outline'"
					style="margin-left: 4.5rem;"
					class="frozenIcon"
					@click="setFrozenColumns('name')"
				></i>
			</template>
			<template #body="slotProps">
				<div class="column-cell" v-tooltip.bottom="slotProps.data.name">
					{{slotProps.data.name}}
				</div>
			</template>
			<template #filter="{filterModel, filterCallback}">
				<InputText style="min-width: 10rem;" class="p-column-filter filter-field" type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" />
			</template>
		</Column>
		<Column field="description" :header="$t('Squeeze.DocumentClasses.Description')" filterField="description" :frozen="frozenColumns.description" :sortable="true" :showFilterMenu="false" style="min-width: 10rem; z-index: 1;">
			<template #header>
				<i :class="frozenColumns.description ? 'mdi mdi-lock-outline' : 'mdi mdi-lock-open-variant-outline'"
					style="margin-left: 8rem;"
					class="frozenIcon"
					@click="setFrozenColumns('description')"
				></i>
			</template>
			<template #body="slotProps">
				<div class="column-cell" v-tooltip.bottom="getTranslatedDescription(slotProps.data)">
					{{ getTranslatedDescription(slotProps.data) }}
				</div>
			</template>
			<template #filter="{filterModel, filterCallback}">
				<InputText style="min-width: 10rem;" class="p-column-filter filter-field" type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" />
			</template>
		</Column>
		<Column field="dataType" :header="$t('Squeeze.DocumentClasses.DataType')" filterField="dataType" :sortable="true" :showFilterMenu="false" style="min-width: 9rem; max-width: 9rem;">
			<template #body="slotProps">
				{{ getTranslationFromDataType(slotProps.data.dataType) }}
			</template>
			<template #filter="{filterModel, filterCallback}">
				<Dropdown
					v-model="filterModel.value"
					@change="filterCallback()"
					:options="dataTypes"
					class="p-column-filter filter-field"
					optionValue="id"
					optionLabel="text"
					:showClear="true"
					style="min-width: 8rem; max-width: 8rem;"
				/>
			</template>
		</Column>
		<Column field="headerLocatorId" :header="$t('Squeeze.DocumentClasses.HeaderLocator')" filterField="headerLocatorId" :sortable="true" :showFilterMenu="false" style="min-width: 10rem;">
			<template #body="slotProps">
				<div class="column-cell" v-tooltip.bottom="getDescriptionFromLocator(slotProps.data.headerLocatorId)">
					{{ getDescriptionFromLocator(slotProps.data.headerLocatorId) }}
				</div>
			</template>
			<template #filter="{filterModel, filterCallback}">
				<InputText style="min-width: 10rem;" class="p-column-filter filter-field" type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" />
			</template>
		</Column>
		<Column field="valueLocatorId" :header="$t('Squeeze.DocumentClasses.ValueLocator')" filterField="valueLocatorId" :sortable="true" :showFilterMenu="false" style="min-width: 10rem;">
			<template #body="slotProps">
				<div class="column-cell" v-tooltip.bottom="getDescriptionFromLocator(slotProps.data.valueLocatorId)">
					{{ getDescriptionFromLocator(slotProps.data.valueLocatorId) }}
				</div>
			</template>
			<template #filter="{filterModel, filterCallback}">
				<Dropdown
					v-model="filterModel.value"
					@change="filterCallback()"
					:options="locators"
					class="p-column-filter filter-field"
					optionValue="id"
					:optionLabel="getTranslatedDescription"
					:showClear="true"
					:filter="true"
					:autoFilterFocus="true"
					style="min-width: 10rem;"
					@show="showDropdownOverlay"
				/>
			</template>
		</Column>
		<Column field="externalName" :header="$t('Squeeze.DocumentClasses.ExternalName')" filterField="externalName" :sortable="true" :showFilterMenu="false" style="min-width: 10rem;">
			<template #body="slotProps">
				<div class="column-cell" v-tooltip.bottom="slotProps.data.externalName">
					{{ slotProps.data.externalName }}
				</div>
			</template>
			<template #filter="{filterModel, filterCallback}">
				<InputText style="min-width: 10rem;" class="p-column-filter filter-field" type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" />
			</template>
		</Column>
		<Column field="mandatory" :header="$t('Squeeze.DocumentClasses.Mandatory')" :sortable="false" filterMatchMode="contains" headerClass="header-center" style="min-width: 6rem; max-width: 6rem; text-align: center;">
			<template #body="slotProps">
				<Checkbox @change="onChangeCheckbox(slotProps.data, 'mandatory')" v-model="slotProps.data.mandatory" :binary="true" />
			</template>
		</Column>
		<Column field="readonly" :header="$t('Squeeze.DocumentClasses.ReadOnly')" :sortable="false" filterMatchMode="contains" headerClass="header-center" style="min-width: 6rem; max-width: 6rem; text-align: center;">
			<template #body="slotProps">
				<Checkbox @change="onChangeCheckbox(slotProps.data, 'readonly')" v-model="slotProps.data.readonly" :binary="true" />
			</template>
		</Column>
		<Column field="hidden" :header="$t('Squeeze.DocumentClasses.Hidden')" :sortable="false" filterMatchMode="contains" headerClass="header-center" style="min-width: 6rem; max-width: 6rem; text-align: center;">
			<template #body="slotProps">
				<Checkbox @change="onChangeCheckbox(slotProps.data, 'hidden')" v-model="slotProps.data.hidden" :binary="true" />
			</template>
		</Column>
		<Column field="forceValidation" :header="$t('Squeeze.DocumentClasses.ForceValidation')" :sortable="false" filterMatchMode="contains" headerClass="header-center" style="min-width: 6rem; max-width: 6rem; text-align: center;">
			<template #body="slotProps">
				<Checkbox @change="onChangeCheckbox(slotProps.data, 'forceValidation')" v-model="slotProps.data.forceValidation" :binary="true" />
			</template>
		</Column>

		<!-- Delete & Edit -->
		<Column style="min-width: 7rem; max-width: 7rem; text-align: right;">
			<template #body="slotProps">
				<Button v-tooltip.bottom="$t('Squeeze.Locators.Edit')" icon="mdi mdi-pencil-outline" class="p-button-lg p-button-rounded p-button-success p-button-text" @click="openEntryDialog(slotProps.data)" />
				<Button v-tooltip.bottom="$t('Squeeze.Validation.Buttons.Delete')" icon="mdi mdi-delete-outline" class="p-button-lg p-button-rounded p-button-danger p-button-text" @click="openDeleteDialog(slotProps.data)" />
			</template>
		</Column>

		<template #footer>
			<Button :label="$t('Squeeze.General.NewEntry')" type="button" icon="mdi mdi-plus" class="p-button" @click="openEntryDialog({})" />
			<Button v-if="isFilterActive" :label="$t('Squeeze.General.ClearFilters')" type="button" icon="mdi mdi-filter-off-outline" class="p-button p-ml-2" @click="clearFilters"/>
			<Button v-tooltip.bottom="$t('Squeeze.Validation.Buttons.ResetTableSettings')" type="button" icon="mdi mdi-sort-variant-remove" class="p-button p-button-text p-ml-2" @click="resetTableSettings" />
		</template>
	</DataTable>
</template>

<script lang="ts">
import {getTranslatedDescription} from "@/util/TranslationHelper";
import {computed, defineComponent, onMounted, PropType, reactive, ref} from 'vue';
import {useI18n} from "vue-i18n";
import {useToast} from "primevue/usetoast";
import DataTable from "@/components/datatable/DexDataTable.vue";
import Column from "primevue/column";
import Checkbox from "primevue/checkbox";
import InputText from "primevue/inputtext";
import Dropdown from "primevue/dropdown";
import {DocumentLocator, DocumentTableColumn} from "@dex/squeeze-client-ts";
import Tooltip from "primevue/tooltip";
import {DocumentFilterObject} from "@/apps/squeeze/interfaces/DocumentSearch";
import {FilterMatchMode} from "primevue/api";
import {showDropdownOverlay} from "@/util/StylesHelper";
import {useRoute} from "vue-router";
import {TableSettings} from "@/util/TableSettings";
import {ClientManager} from "@/singletons/ClientManager";
import useSqueezeStore from "@/apps/squeeze/store";

interface FrozenColumns {
	name: boolean;
	description: boolean;
}

export default defineComponent({
	name: 'DocumentClassTableColumns',
	methods: {showDropdownOverlay},
	components: {
		DataTable,
		Column,
		Checkbox,
		InputText,
		Dropdown,
	},
	props: {
		/** Show loading in the table? */
		loading: {
			type: Boolean,
			default: false,
		},
		/** All rows */
		rows: {
			type: Array as PropType<DocumentTableColumn[]>,
			default: () => [],
		},
		/** All available Locators */
		locators: {
			type: Array as PropType<DocumentLocator[]>,
			default: () => [],
		},
		tableId: {
			type: Number,
			default: 0,
		},
	},
	directives: {
		'tooltip': Tooltip,
	},
	emits: ["openEntryDialog", "deleteEntry", "onChangeCheckbox", "onChangeSortOrder"],
	setup(props, {emit}) {
		const {t} = useI18n();
		const toast = useToast();
		const store = useSqueezeStore();
		const route = useRoute();

		/** User API for persisting table settings */
		const userApi = ClientManager.getInstance().squeeze.user;

		/** Current sort field */
		const currentSortField = ref<string | undefined>(undefined);

		/** Current sort order */
		const currentSortOrder = ref<number>(0);

		/** Data of selected row */
		const rowData = reactive<DocumentTableColumn>({});

		/** Is the Search activated? */
		const searchActive = ref<boolean>(false);

		/** Filters of list (Currently not activated) */
		const filters = ref<DocumentFilterObject>({});

		/** Is Filtering active? */
		const isFilterActive = ref<boolean>(false);

		/** List of Data-Types */
		const dataTypes = computed(() => {
			return [
				{
					id: 'Text',
					text: t('Squeeze.General.Text'),
				},
				{
					id: 'Date',
					text: t('Squeeze.General.Date'),
				},
				{
					id: 'Amount',
					text: t('Squeeze.General.Amount'),
				},
			];
		});

		/** Which columns are frozen? */
		const frozenColumns = reactive<FrozenColumns>({
			name: true,
			description: true,
		});

		/** Set frozen columns */
		const setFrozenColumns = (column: string) => {
			(frozenColumns as any)[column] = !(frozenColumns as any)[column];
			TableSettings.saveTableSubSetting(t, toast, store, userApi, route.name as string, 'frozenColumns', String(props.tableId), { ...frozenColumns });
		}

		/**
		 * Get the Translation of data type
		 * @param {string} type
		 * @return {string} Current Translation of data type
		 */
		const getTranslationFromDataType = (type: string): string => {
			switch (type) {
			case 'Text':
				return t('Squeeze.General.Text');
			case 'Date':
				return t('Squeeze.General.Date');
			case 'Amount':
				return t('Squeeze.General.Amount');
			default:
				return type;
			}
		}

		/**
		 * Opens the Delete Dialog
		 * @param {DocumentTableColumn} data Data from the Table that should be deleted
		 */
		const openDeleteDialog = (data: DocumentTableColumn) => {
			Object.assign(rowData, data);
			emit("deleteEntry", rowData);
		}

		/**
		 * Opens the Entry-Edit-Dialog
		 * @param {DocumentTableColumn} data Data to edit
		 */
		const openEntryDialog = (data: DocumentTableColumn) => {
			if (!data.lookup) {
				data.lookup = {
					active: false,
					allowCustomValues: true,
					tableId: 0,
					resultKeyColumnId: 0,
					searchColumnIds: [],
					resultValueColumnIds: [],
					minInputLength: 1,
				};
			}

			// reset data
			if (!data.id) {
				data.id = undefined;
				data.name = undefined;
				data.description = undefined;
				data.dataType = undefined;
				data.headerLocatorId = undefined;
				data.valueLocatorId = undefined;
				data.mandatory = undefined;
				data.readonly = undefined;
				data.hidden = undefined;
				data.forceValidation = undefined;
				data.sortOrder = undefined;
				data.tableId = undefined;
				data.externalName = undefined;
			}

			data.tableId = props.tableId;
			emit("openEntryDialog", data);
		}

		/**
		 * Gets the Description of a locator
		 * @param {Number} locatorId Id of the Locator to get the Description from
		 * @return {String} Description of the Locator
		 */
		const getDescriptionFromLocator = (locatorId: number) => {
			// No locator at all
			if (locatorId === 0) {
				return ''
			}

			if (!props.locators) {
				return String(locatorId);
			}

			const locator = props.locators.find(locator => locator.id === locatorId);
			if (!locator) {
				return String(locatorId);
			}

			return getTranslatedDescription(locator);
		}

		/** Emits the "deleteEntry"-Event */
		const deleteEntry = () => {
			emit("deleteEntry", rowData);
		}

		/**
		 * Triggered when a checkbox is clicked
		 * @param {DocumentTableColumn} data Data to edit
		 * @param {string} fieldName Name of the current field
		 */
		const onChangeCheckbox = (data: DocumentTableColumn, fieldName: string) => {
			emit("onChangeCheckbox", data, fieldName)
		}

		/**
		 * Triggered when the rows are reordered
		 * @param event
		 */
		const onRowReorder = (event: any) => {
			const orderedList: number[] = [];
			event.value.forEach((column: DocumentTableColumn) => {
				orderedList.push(column.id!);
			});
			emit("onChangeSortOrder", orderedList, props.tableId, isFilterActive.value);
		}

		/**
		 * Triggered when a filter has been entered
		 * @param event
		 */
		const onFilter = (event: any) => {
			isFilterActive.value = event.filteredValue.length < props.rows.length;
		}

		/** Init filters */
		const initFilters = () => {
			filters.value = {
				"id": {value: null, matchMode: FilterMatchMode.EQUALS},
				"name": {value: null, matchMode: FilterMatchMode.CONTAINS},
				"description": {value: null, matchMode: FilterMatchMode.CONTAINS},
				"dataType": {value: null, matchMode: FilterMatchMode.EQUALS},
				"headerLocatorId": {value: null, matchMode: FilterMatchMode.CONTAINS},
				"valueLocatorId": {value: null, matchMode: FilterMatchMode.EQUALS},
				"externalName": {value: null, matchMode: FilterMatchMode.CONTAINS},
			};
		}

		/** Clear filters */
		const clearFilters = () => {
			initFilters();
		}

		initFilters();

		/** Triggered on sort a column */
		const onSort = (event: { sortField: string; sortOrder: number }) => {
			currentSortField.value = event.sortField;
			currentSortOrder.value = event.sortOrder;
			TableSettings.saveTableSubSetting(t, toast, store, userApi, route.name as string, 'sort', String(props.tableId), { field: event.sortField, order: event.sortOrder }, v => !v || v.order === 0);
		}

		/** Reset sort and frozen column settings */
		const resetTableSettings = () => {
			currentSortField.value = undefined;
			currentSortOrder.value = 0;
			TableSettings.saveTableSubSetting(t, toast, store, userApi, route.name as string, 'sort', String(props.tableId), { field: '', order: 0 }, v => !v || v.order === 0);
			Object.assign(frozenColumns, { name: true, description: true });
			TableSettings.saveTableSubSetting(t, toast, store, userApi, route.name as string, 'frozenColumns', String(props.tableId), null, () => true);
		}

		onMounted(() => {
			const savedSort = TableSettings.getTableSubSetting<{ field: string; order: number }>(store, route.name as string, 'sort', String(props.tableId));
			if (savedSort) {
				currentSortField.value = savedSort.field;
				currentSortOrder.value = savedSort.order;
			}

			const savedFrozenColumns = TableSettings.getTableSubSetting<Record<string, boolean>>(store, route.name as string, 'frozenColumns', String(props.tableId));
			if (savedFrozenColumns) {
				Object.assign(frozenColumns, savedFrozenColumns);
			}
		});

		return {
			getTranslatedDescription,
			t,
			toast,
			rowData,
			searchActive,
			filters,
			isFilterActive,
			dataTypes,
			frozenColumns,
			setFrozenColumns,
			getTranslationFromDataType,
			openDeleteDialog,
			openEntryDialog,
			getDescriptionFromLocator,
			deleteEntry,
			onChangeCheckbox,
			onRowReorder,
			onFilter,
			initFilters,
			clearFilters,
			currentSortField,
			currentSortOrder,
			onSort,
			resetTableSettings,
		};
	},
});

</script>

<style lang="scss" scoped>

::v-deep(td div.column-cell) {
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

// Table header
.p-datatable ::v-deep(.p-datatable-thead) {
	z-index: 2;
}

.p-datatable.p-datatable-documents ::v-deep(.p-datatable-thead tr th) {
   background-color: var(--dex-primary-light-color);
   padding: 0.6rem 0.6rem 0.2rem 0.3rem;
   border-bottom-color: var(--dex-primary-light-color);
   color: var(--dex-text-color);
}

.p-datatable.p-datatable-documents ::v-deep(.p-datatable-thead tr th:first-child) {
	padding: 0.6rem 0.6rem 0.2rem 0.6rem;
}

.p-datatable.p-datatable-documents ::v-deep(.p-datatable-thead .p-sortable-column-icon) {
	color: var(--dex-text-color);

	&:hover {
		color: var(--dex-text-color);
	}
}

.p-datatable.p-datatable-documents ::v-deep(.p-datatable-thead .p-inputtext.filter-field) {
	padding: 0.4rem;
	background-color: transparent;
	color: var(--dex-text-color);
	border-radius: 0;
	border-color: var(--filter-field-color);
	border-style: solid;
	border-width: 0.063rem;

	&:focus {
		border-color: rgba(255, 255, 255, 0.8);
	}
}

.p-datatable ::v-deep(.p-datatable-thead .p-dropdown.filter-field) {
	padding:0.4rem;
	background-color: transparent;
	color: var(--dex-text-color);
	border-radius: 0;
	border-color: var(--filter-field-color);

	&:focus {
		border-color: rgba(255, 255, 255, 0.8);
	}

	.p-dropdown-label {
		padding: 0 1rem 0 0.4rem;
		color: var(--filter-field-color);
	}
}

::v-deep(.header-center div.p-column-header-content) {
	justify-content: center;
}

.frozenIcon {
	color: var(--dex-text-color);
	position: absolute;

	&:hover {
		color: var(--text-color);
		opacity: 0.75;
	}
}

</style>
